import React, { useState, useRef, useEffect } from "react";
import { Diamond, AlertCircle, CheckCircle } from "lucide-react";
import { getTimelinePosition } from "../../dashboard/utils/timeline";
import { dateOnlyFormatted } from "../../../utils/systemSettings";

const KEY_EVENT_TYPES = {
  TARGET_DATE: 0,
  DATE_RANGE: 1,
};

const KEY_EVENT_REF_TYPES = {
  PROGRAM: 1,
  PROJECT: 2,
};

const KEY_EVENT_ICON_TYPES = {
  CUSTOM: 0,
  MILESTONE: 1,
  DEADLINE: 2,
  REVIEW: 3,
};

const KEY_EVENT_ICONS = {
  [KEY_EVENT_ICON_TYPES.MILESTONE]: { icon: Diamond, color: "#9333EA" },
  [KEY_EVENT_ICON_TYPES.DEADLINE]: { icon: AlertCircle, color: "#DC2626" },
  [KEY_EVENT_ICON_TYPES.REVIEW]: { icon: CheckCircle, color: "#059669" },
};

const KeyEventMarkers = ({
  keyEvents = [],
  timelineData,
  viewType,
  displayYearRange,
  expandedProjects = {},
  dynamicWidths = null,
}) => {
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const settings = JSON.parse(localStorage.getItem("settings"));
  const canvasRef = useRef(null);

  useEffect(() => {
    // Create hidden canvas for text measurement
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
  }, []);

  const measureTextWidth = (text) => {
    if (!canvasRef.current) return 0;
    
    const context = canvasRef.current.getContext('2d');
    // Match the exact styling from the component
    context.font = '500 12px Figtree, sans-serif';
    return context.measureText(text).width;
  };

  if (!keyEvents || keyEvents.length === 0) return null;

  const getEventPosition = (event) => {
    if (event.type === KEY_EVENT_TYPES.TARGET_DATE) {
      const { left, shouldRender } = getTimelinePosition(
        event.endDate,
        event.endDate,
        viewType,
        new Date(),
        displayYearRange,
        dynamicWidths
      );
      return { left, width: 0, shouldRender, isRange: false };
    } else {
      const { left, width, shouldRender } = getTimelinePosition(
        event.startDate,
        event.endDate,
        viewType,
        new Date(),
        displayYearRange,
        dynamicWidths
      );
      return { left, width, shouldRender, isRange: true };
    }
  };

  const findEventRow = (event) => {
    let programRowIndex = 0;
    let projectRowIndex = -1;
    let topPosition = 0;

    for (let i = 0; i < timelineData.length; i++) {
      const program = timelineData[i];

      // Check if event is attached to this program
      if (event.refType === KEY_EVENT_REF_TYPES.PROGRAM && event.refId === program.id) {
        return { topPosition, found: true };
      }

      // Add program row height
      topPosition += 52;

      // Check if program is expanded and has projects
      if (expandedProjects[program.id] && program.projects) {
        for (let j = 0; j < program.projects.length; j++) {
          const project = program.projects[j];

          // Check if event is attached to this project
          if (event.refType === KEY_EVENT_REF_TYPES.PROJECT && event.refId === project.id) {
            return { topPosition, found: true };
          }

          // Add project row height
          topPosition += 52;
        }
      }
    }

    return { topPosition: 0, found: false };
  };

  const handleMouseEnter = (event, e) => {
    setHoveredEvent(event);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handleMouseLeave = () => {
    setHoveredEvent(null);
  };

  return (
    <>
      {keyEvents.map((event) => {
        const { topPosition, found } = findEventRow(event);
        if (!found) return null;

        const { left, width, shouldRender, isRange } = getEventPosition(event);
        if (!shouldRender) return null;

        const iconType = event.iconType ?? KEY_EVENT_ICON_TYPES.MILESTONE;
        const { icon: IconComponent, color: iconColor } = KEY_EVENT_ICONS[iconType] || KEY_EVENT_ICONS[KEY_EVENT_ICON_TYPES.MILESTONE];

        if (isRange) {
          // Calculate available space inside the range bar
          // Icon: 12px + left padding: 4px (left-1) = 16px
          // Right padding: 4px (right-1) = 4px
          // Border: 2px * 2 = 4px
          // Total overhead: 24px
          const availableWidth = width - 24;
          const textWidth = measureTextWidth(event.title);
          const titleFitsInside = textWidth <= availableWidth;

          return (
            <div
              key={event.id}
              className="absolute"
              style={{
                left: `${left - 5}px`,
                top: `${topPosition + 5}px`,
                height: "20px",
                zIndex: 15,
                display: "flex",
                alignItems: "center",
                pointerEvents: "auto",
              }}
              onMouseEnter={(e) => handleMouseEnter(event, e)}
              onMouseLeave={handleMouseLeave}
            >
              {/* Range bar */}
              <div
                className="cursor-pointer transition-all hover:opacity-80"
                style={{
                  width: `${width}px`,
                  height: "20px",
                  backgroundColor: `${iconColor}30`,
                  border: `2px solid ${iconColor}`,
                  borderRadius: "8px",
                  position: "relative",
                }}
              >
                <div
                  className="absolute left-1 top-1/2 -translate-y-1/2"
                  style={{ color: iconColor }}
                >
                  <IconComponent size={12} color={iconColor} strokeWidth={2} />
                </div>
                
                {titleFitsInside && (
                  <div
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-xs font-medium truncate"
                    style={{
                      color: iconColor,
                      maxWidth: `${availableWidth}px`,
                      fontFamily: "Figtree, sans-serif",
                    }}
                  >
                    {event.title}
                  </div>
                )}
              </div>

              {/* Title outside when it doesn't fit */}
              {!titleFitsInside && (
                <div
                  className="text-xs font-medium whitespace-nowrap"
                  style={{
                    color: iconColor,
                    fontFamily: "Figtree, sans-serif",
                    marginLeft: "2px",
                  }}
                >
                  {event.title}
                </div>
              )}
            </div>
          );
        } else {
          return (
            <div
              key={event.id}
              className="absolute cursor-pointer transition-all hover:scale-110"
              style={{
                left: `${left - 17}px`,
                top: `${topPosition + 35}px`,
                transform: 'translateY(-50%)',
                zIndex: 15,
                pointerEvents: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "2px",
              }}
              onMouseEnter={(e) => handleMouseEnter(event, e)}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className="rounded-full shadow-lg"
                style={{
                  backgroundColor: iconColor,
                  padding: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconComponent size={12} color="white" strokeWidth={2.5} />
              </div>
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: iconColor,
                  fontFamily: "Figtree, sans-serif",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "center",
                  lineHeight: "1",
                }}
              >
                {event.title}
              </span>
            </div>
          );
        }
      })}

      {/* Compact Tooltip */}
      {hoveredEvent && (
        <div
          className="fixed z-[100] pointer-events-none"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 10}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-[220px]"
            style={{ fontFamily: "Figtree, sans-serif" }}
          >
            <div className="flex items-start gap-2 mb-2">
              {(() => {
                const iconType = hoveredEvent.iconType ?? KEY_EVENT_ICON_TYPES.MILESTONE;
                const { icon: IconComponent, color: iconColor } = KEY_EVENT_ICONS[iconType] || KEY_EVENT_ICONS[KEY_EVENT_ICON_TYPES.MILESTONE];
                return <IconComponent size={14} color={iconColor} strokeWidth={2} className="mt-0.5 flex-shrink-0" />;
              })()}
              <h4 className="font-semibold text-sm text-gray-900 flex-1">
                {hoveredEvent.title}
              </h4>
            </div>

            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 w-12">Date:</span>
                <span className="font-medium text-gray-700">
                  {hoveredEvent.type === KEY_EVENT_TYPES.TARGET_DATE
                    ? dateOnlyFormatted(settings, hoveredEvent.endDate)
                    : `${dateOnlyFormatted(settings, hoveredEvent.startDate)} - ${dateOnlyFormatted(settings, hoveredEvent.endDate)}`}
                </span>
              </div>

              {hoveredEvent.customFields && hoveredEvent.customFields.length > 0 && (
                <>
                  <div className="border-t border-gray-200 my-2 pt-2">
                    {hoveredEvent.customFields.map((field, index) => (
                      <div key={index} className="flex items-start gap-2 mb-1">
                        <span className="text-gray-500 w-12 flex-shrink-0">{field.name}:</span>
                        <span className="font-medium text-gray-700 flex-1">{field.value}</span>
                      </div>
                    ))} 
                  </div>
                </>
              )}
            </div>

            {/* Arrow pointing down */}
            <div
              className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
              style={{
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: "6px solid white",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default KeyEventMarkers;
